
import { useEffect, useState } from 'react';
import { useTravelNews } from '@/hooks/useTravelNews';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ExternalLink, ArrowRight, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { LazyImage } from "@/components/ui/lazy-image";
import { format } from 'date-fns';
import { NewsCard } from '../news/NewsCard';
import { useIsMobile } from '@/hooks/use-mobile';

export function TravelNewsSection() {
  // Reduce cache duration to get fresher content and increase the limit to have more options
  const { news, isLoading, error, refetch } = useTravelNews({ 
    limit: 20,
    cacheDuration: 5 * 60 * 1000 // 5 minutes cache for homepage
  });
  const [displayNews, setDisplayNews] = useState<Array<any>>([]);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (news.length > 0) {
      // Select the most recent news items (by published_at)
      const sortedNews = [...news].sort((a, b) => {
        return new Date(b.published_at).getTime() - new Date(a.published_at).getTime();
      });
      
      // Take the top news items (2 for mobile, 3 for desktop)
      const count = isMobile ? 2 : 3;
      const latestNews = sortedNews.slice(0, count);
      setDisplayNews(latestNews);
    }
  }, [news, isMobile]);

  // Add a manual refetch function to force refresh the news
  const handleRefresh = () => {
    refetch();
  };

  if (isLoading) {
    return (
      <div className="w-full space-y-4 px-4 sm:px-0">
        <h2 className="text-2xl font-bold sm:text-3xl">Latest Travel News</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {Array.from({ length: isMobile ? 2 : 3 }).map((_, index) => (
            <Card key={index} className="overflow-hidden">
              <CardHeader className="p-5 pb-2">
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-4 w-3/4 mt-2" />
              </CardHeader>
              <CardContent className="p-5 pt-0">
                <Skeleton className="h-24 w-full" />
              </CardContent>
              <CardFooter className="p-5 pt-0">
                <Skeleton className="h-11 w-full" />
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error || displayNews.length === 0) {
    return (
      <div className="w-full py-8 px-4 sm:px-0">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold sm:text-3xl">Latest Travel News</h2>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleRefresh} size="sm" className="h-10">
              <RefreshCw className="mr-2 h-4 w-4" />
              <span>Refresh</span>
            </Button>
            <Link to="/travel-news" aria-label="View all travel news">
              <Button variant="ghost" className="flex items-center gap-1 h-10" size="sm">
                <span>View All</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
        <div className="text-center p-8 border rounded-lg bg-muted/30">
          <p className="text-muted-foreground text-base">
            {error ? "Failed to load news. Please try again later." : "No news articles available."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full py-8 px-4 sm:px-0">
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <h2 className="text-2xl font-bold sm:text-3xl">Latest Travel News</h2>
        <div className="flex gap-2 items-center">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh} 
            className="hidden sm:flex h-10"
            aria-label="Refresh news"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            <span>Refresh</span>
          </Button>
          <Link to="/travel-news" aria-label="View all travel news">
            <Button variant="ghost" className="flex items-center gap-1 h-10" size="sm">
              <span className="text-base">View All</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
        {displayNews.map((item) => (
          <NewsCard
            key={item.id}
            title={item.title}
            description={item.description}
            url={item.url}
            imageUrl={item.image_url}
            source={item.source}
            publishedAt={item.published_at}
          />
        ))}
      </div>
    </div>
  );
}

export default TravelNewsSection;
